/**
 * @description
 * API key list tab component for displaying Anthropic API keys.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import type { ApiKeyListProps } from "./types";

export function ApiKeyList({
  apiKeys,
  workspaces,
  loading: _loading,
  onRefresh,
}: ApiKeyListProps) {
  const getWorkspaceName = (workspaceId: string | null): string => {
    if (!workspaceId) return "N/A";
    const workspace = workspaces.find((w) => w.anthropicId === workspaceId);
    return workspace ? workspace.name : workspaceId;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">API Keys</h2>
        <button
          onClick={onRefresh}
          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-2">
        {apiKeys.map((key) => (
          <div
            key={key.id}
            className="flex items-center justify-between rounded-md border p-4"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <p className="font-medium">{key.name}</p>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    key.status === "active"
                      ? "bg-green-100 text-green-800"
                      : key.status === "inactive"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {key.status}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground font-mono">
                {key.partial_key_hint || "N/A"}
              </p>
              {key.workspace_id && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Workspace: {getWorkspaceName(key.workspace_id)}
                </p>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              Created: {new Date(key.created_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
